import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext'; 
import { isPro as checkIsPro, isPremium as checkIsPremium } from '../../../../utils/planPermissions';
import { canOpenPath, PATH_MODULE } from '../../../../utils/modulePermissions';
import { checkinRequestsApi } from '../../../../api/checkinRequestsApi';
import { cardScanApi } from '../../../../api/cardScanApi';
import { useClientRequestsInfo } from '../../../../api/clientRequestsApi';
import {
    Home, Store, Users, UserPlus, Coins,
    Trophy, Gift, Ticket, UserMinus,
    History, Settings, Crown, Tablet, BarChart3,
    Headset, Cake, UserCheck, MessageSquare, HelpCircle,
    ThumbsDown, Phone, Share2,
    Tag, Send,
    CreditCard,  // ✅ Gift Cards
    QrCode,      // registro por QR (F1)
    ScanLine,    // tarjeta QR del cliente (23-oct-2026)
 ListTodo, Inbox, BadgeCheck, UsersRound, CalendarClock, Bike } from 'lucide-react';

interface StandardNavigationProps {
    currentPath: string;
}

// ── Tipos ──
interface NavItem {
    path: string;
    icon: React.ElementType;
    label: string;
    priority?: boolean;
    disabledUntilOnboarding?: boolean;
    isPro?: boolean;
    isPremium?: boolean;
    // Gate por MÓDULO del motor de permisos (nivel efectivo del operador, ver
    // utils/modulePermissions). Se aplica en AND con los gates por plan.
    mod?: string;
    // Ancla del tour de bienvenida (módulo cross src/lib/tour): data-tour="<valor>".
    tour?: string;
}

interface NavSection {
    label: string;
    items: NavItem[];
}

// Visitas del QR esperando confirmación. Se sondea el contador (endpoint barato) para que
// el negocio vea que hay algo que atender sin entrar a la pantalla: sin esto, la cola es
// invisible y las solicitudes caducan a las 24 h sin que nadie se entere.
// Última respuesta, fuera del componente: cada página monta su propio <Layout> y el menú se vuelve a
// crear en cada clic. Sin esto «Visitas por confirmar» aparecía un instante (enabled=null) y se iba al
// contestar el servidor, y todo el menú brincaba 40 px (2-oct-2026, Carlos: «efecto raro de expansión»).
type CheckinsState = { pending: number; enabled: boolean | null };
let lastCheckins: CheckinsState = { pending: 0, enabled: null };

function usePendingCheckins() {
  // `enabled` empieza en null = "todavía no sé" solo la primera vez; después, lo último que se supo.
  const [state, setState] = useState<CheckinsState>(lastCheckins);
  useEffect(() => {
    let alive = true;
    const tick = () => checkinRequestsApi.count()
      .then(r => { lastCheckins = { pending: Number(r.pending) || 0, enabled: !!r.enabled }; if (alive) setState(lastCheckins); })
      .catch(() => { /* silencioso: es un adorno, no debe romper el menú */ });
    tick();
    const t = window.setInterval(tick, 60000);
    window.addEventListener('lealify:qr-checkin-changed', tick); // al prender/apagar el QR
    return () => { alive = false; window.clearInterval(t); window.removeEventListener('lealify:qr-checkin-changed', tick); };
  }, []);
  return state;
}

// Tarjeta QR del cliente (23-oct-2026): igual que el QR, la entrada se ve siempre; apagada lleva GRATIS.
let lastCardScan: boolean | null = null;
function useCardScanEnabled() {
  const [enabled, setEnabled] = useState<boolean | null>(lastCardScan);
  useEffect(() => {
    let alive = true;
    const tick = () => cardScanApi.resumen()
      .then(r => { lastCardScan = !!r.enabled; if (alive) setEnabled(lastCardScan); })
      .catch(() => { /* adorno */ });
    tick();
    window.addEventListener('lealify:card-scan-changed', tick);
    return () => { alive = false; window.removeEventListener('lealify:card-scan-changed', tick); };
  }, []);
  return enabled;
}

const StandardNavigation: React.FC<StandardNavigationProps> = ({ currentPath }) => {
    const navigate = useNavigate();
    const { pending: pendingCheckins, enabled: qrEnabled } = usePendingCheckins();
    const cardScanEnabled = useCardScanEnabled();
    // Globo de «Mis peticiones»: las que esperan un dato del negocio (11-oct-2026).
    // «Visitas por QR» se ve SIEMPRE (8-oct-2026): escondida hasta prender el QR, nadie la descubría
    // (38 de 377 negocios). Apagado lleva la etiqueta GRATIS y la pantalla invita a activarlo.
    const showCheckins = true;
    const { needsOnboarding, currentUser } = useAuth();
    // «Mis peticiones»: el servidor dice quién la usa (dueño/encargado o permiso por persona).
    const { waiting: requestsWaiting, puede: puedePeticiones } = useClientRequestsInfo();

    // Check if user has PRO or Premium
    const hasProAccess = checkIsPro(currentUser?.subscription);
    const hasPremiumAccess = checkIsPremium(currentUser?.subscription);

    // ── Navegación organizada por secciones ──
    // F4 reorden (24-oct-2026): grupos por PREGUNTA del dueño, no por «tipo» de función.
    //   CLIENTES  ¿quiénes son?        · VISITAS   ¿cómo cuento y pago la visita de hoy?
    //   PROGRAMA  ¿qué les doy?        · MENSAJES  ¿qué les digo?
    //   ANÁLISIS  ¿cómo voy?           · AJUSTES   ¿quién entra y qué pago yo?
    // Antes: Operaciones / Comunicación / Configuración, con «Visitas por QR» y «Escanear tarjeta» perdidos
    // entre los listados de clientes. Las RUTAS no cambian (nada que redirigir); el aviso único
    // «Reordenamos el menú» (components/dashboard/AvisoMenuNuevo) dice dónde quedó cada cosa.
    // Repartos (28-oct-2026, Cenizas): con el flag del negocio `nav_first`, la entrada va la PRIMERA del menú (arriba de Inicio);
    // sin él, se queda en «Programa» como siempre.
    const repartosItem: NavItem = { path: '/dashboard/repartos', icon: Bike, label: 'Repartos', disabledUntilOnboarding: true, mod: 'delivery' };
    const repartosPrimero = !!currentUser?.deliveryNavFirst;
    const navSections: NavSection[] = [
        {
            label: 'Inicio',
            items: [
                ...(repartosPrimero ? [repartosItem] : []),
                { path: '/dashboard', icon: Home, label: 'Inicio' },
                { path: '/dashboard/business', icon: Store, label: 'Mi Negocio', priority: true },
            ],
        },
        {
            label: 'Clientes',
            items: [
                { path: '/dashboard/clients', icon: Users, label: 'Lista de Clientes', disabledUntilOnboarding: true },
                { path: '/dashboard/register-client', icon: UserPlus, label: 'Registrar Cliente', disabledUntilOnboarding: true, tour: 'nav-register-client' },
                { path: '/dashboard/latest-registrations', icon: UserCheck, label: 'Últimos Registros', disabledUntilOnboarding: true },
                { path: '/dashboard/best-clients', icon: Trophy, label: 'Mejores Clientes', disabledUntilOnboarding: true },
                { path: '/dashboard/inactive-clients', icon: UserMinus, label: 'Clientes Inactivos', disabledUntilOnboarding: true, isPro: true },
                { path: '/dashboard/unsatisfied-clients', icon: ThumbsDown, label: 'Clientes Insatisfechos', disabledUntilOnboarding: true, isPro: true },
                { path: '/dashboard/birthday-panel', icon: Cake, label: 'Cumpleaños', disabledUntilOnboarding: true, isPro: true },
            ],
        },
        {
            // Todo lo que pasa EN EL MOSTRADOR con el cliente delante: registrar la visita (QR, tarjeta), canjear, ajustar puntos.
            label: 'Visitas',
            items: [
                { path: '/dashboard/latest-visits', icon: History, label: 'Últimas Visitas', disabledUntilOnboarding: true },
                { path: '/dashboard/checkin-approvals', icon: QrCode, label: 'Visitas por QR', disabledUntilOnboarding: true },
                { path: '/dashboard/card-scan', icon: ScanLine, label: 'Escanear tarjeta', disabledUntilOnboarding: true },
                { path: '/dashboard/redeem-rewards', icon: Ticket, label: 'Canjear Recompensas', disabledUntilOnboarding: true },
                { path: '/dashboard/recharge-points', icon: Coins, label: 'Gestionar Puntos', disabledUntilOnboarding: true, isPro: true },
            ],
        },
        {
            // Lo que el negocio OFRECE y cómo lo organiza. Cada módulo es UNA entrada con pestañas dentro (F3).
            label: 'Programa',
            items: [
                { path: '/dashboard/rewards', icon: Gift, label: 'Recompensas', disabledUntilOnboarding: true, tour: 'nav-rewards' },
                // Membresías F1 (20-oct-2026): solo con el flag del negocio (loyalty_flags.memberships), ver filtro abajo.
                { path: '/dashboard/memberships', icon: BadgeCheck, label: 'Membresías', disabledUntilOnboarding: true, isPro: true },
                // «Config. Gift Cards» (F3 reorden 24-oct-2026) es la pestaña Ajustes de Gift Cards; /dashboard/gift-card-config redirige.
                { path: '/dashboard/gift-cards', icon: CreditCard, label: 'Gift Cards', disabledUntilOnboarding: true, isPro: true },
                { path: '/dashboard/referrals', icon: Share2, label: 'Referidos', disabledUntilOnboarding: true, isPro: true },
                { path: '/dashboard/tags', icon: Tag, label: 'Etiquetas', disabledUntilOnboarding: true, isPro: true },
                { path: '/dashboard/tasks', icon: ListTodo, label: 'Tareas', disabledUntilOnboarding: true, mod: 'tasks' },
                // Repartos (módulo `delivery`, apagado por defecto: solo lo ve el negocio que lo tiene contratado).
                ...(repartosPrimero ? [] : [repartosItem]),
            ],
        },
        {
            label: 'Mensajes',
            items: [
                { path: '/dashboard/campaigns', icon: MessageSquare, label: 'Campañas', disabledUntilOnboarding: true, isPro: true, tour: 'nav-campaigns' },
                // F2 reorden (24-oct-2026): bienvenida, visita, referidos, alerta de opinión, saldo bajo, reseñas en Google y los
                // 2 avisos de membresía eran 8 entradas del mismo editor; ahora UNA lista (/dashboard/mensajes). Las rutas viejas redirigen.
                { path: '/dashboard/mensajes', icon: Send, label: 'Mensajes automáticos', disabledUntilOnboarding: true, isPro: true },
                // Antes «WhatsApp Directo» (F4): es la línea desde la que salen los mensajes, el nombre lo dice.
                { path: '/dashboard/whatsapp', icon: Phone, label: 'WhatsApp del negocio', disabledUntilOnboarding: true, isPro: true },
            ],
        },
        {
            label: 'Análisis',
            items: [
                { path: '/dashboard/analytics', icon: BarChart3, label: 'Analíticas', disabledUntilOnboarding: true },
                // Reporte «Por empleado» (23-oct-2026, Hogar China City): solo dueño/gerente (filtro abajo); PRO con el equipo.
                { path: '/dashboard/staff-report', icon: UsersRound, label: 'Por empleado', disabledUntilOnboarding: true, isPro: true },
                // Corte diario (F1 reorden 24-oct-2026): es un REPORTE, salió de Mi Negocio › Programa; solo dueño (filtro abajo).
                { path: '/dashboard/daily-cut', icon: CalendarClock, label: 'Corte diario', disabledUntilOnboarding: true },
            ],
        },
        {
            label: 'Ajustes',
            items: [
                { path: '/dashboard/team', icon: UserCheck, label: 'Equipo', disabledUntilOnboarding: true, mod: 'users_admin', isPro: true },
                { path: '/dashboard/subscription', icon: Crown, label: 'Suscripción', disabledUntilOnboarding: true },
                // «Términos Legales» (F1 reorden 24-oct-2026) vive dentro de Mi Negocio › Kiosco; /dashboard/legal redirige.
                // Antes «Configuración» (F4): es la cuenta de QUIEN entra (nombre, foto, contraseña), no la del negocio.
                { path: '/dashboard/settings', icon: Settings, label: 'Mi cuenta', disabledUntilOnboarding: true },
            ],
        },
        {
            // «Mis peticiones» con entrada propia (11-oct-2026, como en la agenda: fuera de Ayuda para que se
            // vea). Sin disabledUntilOnboarding: quien se atora configurando también tiene que poder pedir ayuda.
            label: 'Ayuda',
            items: [
                { path: '/dashboard/peticiones', icon: Inbox, label: 'Mis peticiones' },
                { path: '/dashboard/faq', icon: HelpCircle, label: 'Preguntas Frecuentes' },
            ],
        },
    ];

    // Function to handle external link
    const handleExternalLink = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="space-y-1">
            {navSections.map((section, sectionIndex) => {
                // Filtrar items premium del array de esta sección + gate por módulo
                // (nivel efectivo del operador; sin módulos en contexto = no oculta).
                // El módulo sale del item.mod explícito o del mapa central PATH_MODULE.
                const visibleItems = section.items.filter((item) => {
                    const mod = item.mod ?? PATH_MODULE[item.path];
                    // La cola del QR se oculta si el negocio no lo usa (y no dejó pendientes).
                    if (item.path === '/dashboard/checkin-approvals' && !showCheckins) return false;
                    if (item.path === '/dashboard/peticiones' && !puedePeticiones) return false;
                    if (item.path === '/dashboard/staff-report' && currentUser?.baseRole && !['owner', 'manager'].includes(currentUser.baseRole)) return false;
                    if (item.path === '/dashboard/daily-cut' && currentUser?.baseRole && currentUser.baseRole !== 'owner') return false;   // manda el Excel con teléfonos: solo el dueño
                    if (item.path === '/dashboard/memberships' && !currentUser?.memberships) return false;   // flag por negocio (membresías F1)
                    return (!item.isPremium || hasPremiumAccess) &&
                        canOpenPath(currentUser, item.path, mod);
                });

                // Si no hay items visibles en la sección, no renderizarla
                if (visibleItems.length === 0) return null;

                return (
                    <div key={section.label}>
                        {/* Separador de sección (no en la primera) */}
                        {sectionIndex > 0 && (
                            <div className="pt-3 pb-1 px-4">
                                <p className="text-[10px] font-semibold text-content-3 uppercase tracking-wider" data-testid="nav-grupo">
                                    {section.label}
                                </p>
                            </div>
                        )}

                        {/* Items de la sección */}
                        {visibleItems.map((item) => {
                            const isDisabled = needsOnboarding && item.disabledUntilOnboarding && item.path !== '/dashboard/business';

                            return (
                                <button
                                    key={item.path}
                                    data-tour={item.tour}
                                    data-testid="nav-item"
                                    data-path={item.path}
                                    data-grupo={section.label}
                                    onClick={() => !isDisabled && navigate(item.path)}
                                    className={`w-full group relative flex items-center px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ease-in-out ${
                                        isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                    style={{
                                        // --nav-active-*: la barra de marca (.sidebar-marca) pinta la opción activa como píldora azul sólida.
                                        backgroundColor: currentPath === item.path ? 'var(--nav-active-bg, rgb(var(--c-accent) / 0.16))' : '',
                                        color: currentPath === item.path ? 'var(--nav-active-fg, rgb(var(--c-accent)))' : 'rgb(var(--c-content))',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (currentPath !== item.path && !isDisabled)
                                            e.currentTarget.style.backgroundColor = 'rgb(var(--c-surface-2))';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (currentPath !== item.path && !isDisabled)
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                    disabled={isDisabled}
                                    title={
                                        isDisabled
                                            ? 'Completa la configuración de tu negocio primero'
                                            : item.isPro && !hasProAccess
                                              ? 'Función exclusiva para usuarios PRO'
                                              : ''
                                    }
                                >
                                    <item.icon
                                        className={`mr-3 h-5 w-5 flex-shrink-0 ${
                                            isDisabled ? 'text-content-3' : currentPath === item.path ? '' : 'text-content-3'
                                        }`}
                                        style={{
                                            color: currentPath === item.path ? 'var(--nav-active-fg, rgb(var(--c-accent)))' : undefined,
                                        }}
                                    />
                                    <span className="transition-colors duration-200 ease-in-out flex-grow text-left">
                                        {item.label}
                                    </span>

                                    {/* Visitas del QR por confirmar */}
                                    {item.path === '/dashboard/checkin-approvals' && pendingCheckins > 0 && (
                                        <span
                                            className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold"
                                            title={`${pendingCheckins} visita(s) por confirmar`}
                                        >
                                            {pendingCheckins > 99 ? '99+' : pendingCheckins}
                                        </span>
                                    )}

                                    {/* Peticiones que esperan respuesta del negocio */}
                                    {item.path === '/dashboard/peticiones' && requestsWaiting > 0 && (
                                        <span
                                            className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold"
                                            title={`${requestsWaiting} esperando tu respuesta`}
                                            data-testid="peticiones-globo"
                                        >
                                            {requestsWaiting > 99 ? '99+' : requestsWaiting}
                                        </span>
                                    )}

                                    {item.path === '/dashboard/checkin-approvals' && qrEnabled === false && pendingCheckins === 0 && (
                                        <span className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-green-400/20 text-green-300">Gratis</span>
                                    )}
                                    {item.path === '/dashboard/card-scan' && cardScanEnabled === false && (
                                        <span className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-green-400/20 text-green-300">Gratis</span>
                                    )}

                                    {/* Show PRO badge for PRO features only if user doesn't have access.
                                        El tooltip va en el <span>: Lucide no acepta `title`, y como
                                        atributo del <svg> tampoco generaría tooltip. */}
                                    {item.isPro && !hasProAccess && (
                                        <span title="Función exclusiva para usuarios PRO" className="inline-flex">
                                            <Crown className="h-4 w-4 text-yellow-500 ml-2 animate-pulse" />
                                        </span>
                                    )}

                                    {/* Notification dot for the business configuration item if onboarding is needed */}
                                    {item.path === '/dashboard/business' && needsOnboarding && (
                                        <span
                                            className="absolute top-1/2 right-2 transform -translate-y-1/2 h-2 w-2 bg-red-500 rounded-full animate-pulse"
                                            title="Configuración pendiente"
                                            aria-hidden="true"
                                        ></span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                );
            })}

            {/* ── Links externos ── */}
            <div className="pt-3 pb-1 px-4">
                <p className="text-[10px] font-semibold text-content-3 uppercase tracking-wider">
                    Accesos rápidos
                </p>
            </div>

            {/* External Link to Visits Panel */}
            <button
                onClick={() => handleExternalLink('https://app.lealify.com')}
                className="w-full group relative flex items-center px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ease-in-out cursor-pointer bg-accent/10 hover:bg-accent/20 border border-accent/30"
            >
                <Tablet className="mr-3 h-5 w-5 text-accent flex-shrink-0" />
                <span className="text-accent flex-grow text-left">Kiosco de Visitas</span>
                <span className="text-xs text-accent/60">↗</span>
            </button>

            {/* Support Button */}
            <button
                onClick={() => handleExternalLink('https://wa.me/526861326204?text=Hola%20necesito%20ayuda%20con%20Lealify')}
                className="w-full group relative flex items-center px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ease-in-out cursor-pointer hover:bg-canvas"
            >
                <Headset className="mr-3 h-5 w-5 text-content-3 flex-shrink-0" />
                <span className="text-content-2 flex-grow text-left">Soporte</span>
                <span className="text-xs text-content-3">↗</span>
            </button>
        </div>
    );
};

export default StandardNavigation;